import React from "react";
import ReactMarkdown from "react-markdown";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import { useTranslation } from "react-i18next";
import { Icon } from "@repo/ui";
import { useTheme } from "@repo/tokens";
import type { RevisePhase } from "../../../hooks/useDoubtBuddyRevise";
import {
  extractFallbackFinalAnswer,
  extractToRagSolution,
} from "./doubtBuddyExtractSections";
import { normalizeMarkdownMath } from "./MarkdownInline";
import { SolutionLoadingBubble } from "./SolutionLoadingBubble";

const mdPlugins = { remarkPlugins: [remarkMath], rehypePlugins: [rehypeKatex] };

interface MarkdownSolutionProps {
  phase: RevisePhase;
  finalMarkdown: string | null;
  error: string | null;
  loadingLabel?: string;
  /** When true, bubble uses AINA stack intro spacing inside `.gac-assistant-stack-body` */
  loadingInAinaStack?: boolean;
  onSolutionRevealed?: () => void;
  onDoneResolved?: () => void;
  forceFullSolution?: boolean;
}

export function MarkdownSolution({
  phase,
  finalMarkdown,
  error,
  loadingLabel,
  loadingInAinaStack = false,
  onSolutionRevealed,
  onDoneResolved,
  forceFullSolution = false,
}: MarkdownSolutionProps) {
  const { theme } = useTheme();
  const { t } = useTranslation("doubts");
  const loadingText = loadingLabel ?? t("chat_loading_a11y");

  const [currentStepShowing, setCurrentStepShowing] =
    React.useState<number>(-1);
  const [showAllSteps, setShowAllSteps] = React.useState(false);
  const [showSolution, setShowSolution] = React.useState(false);
  const solutionRevealedFiredRef = React.useRef(false);

  React.useEffect(() => {
    solutionRevealedFiredRef.current = false;
    setCurrentStepShowing(-1);
    setShowAllSteps(false);
    setShowSolution(false);
  }, [finalMarkdown]);

  React.useEffect(() => {
    if (!forceFullSolution || phase !== "complete") return;
    const md = typeof finalMarkdown === "string" ? finalMarkdown.trim() : "";
    if (!md) return;
    if (solutionRevealedFiredRef.current) return;
    solutionRevealedFiredRef.current = true;
    onSolutionRevealed?.();
  }, [forceFullSolution, phase, finalMarkdown, onSolutionRevealed]);

  if (phase === "error" && error) {
    return (
      <div className="gac-md-error" role="alert">
        <p>{error}</p>
      </div>
    );
  }

  if (
    phase === "connecting" ||
    phase === "processing" ||
    phase === "streaming"
  ) {
    return (
      <SolutionLoadingBubble
        patienceLabel={t("please_be_patient_solution")}
        loadingAriaLabel={loadingText}
        inAinaStack={loadingInAinaStack}
      />
    );
  }

  if (phase !== "complete" || !finalMarkdown) {
    return null;
  }

  const { hints, steps, answer } = extractToRagSolution(finalMarkdown);
  const fallbackAnswer = extractFallbackFinalAnswer(finalMarkdown);
  const displayAnswer = answer.length > 0 ? answer : fallbackAnswer.trim();
  const hasHints = hints.length > 0;
  const hasSteps = steps.length > 0;
  const hasAnswer = displayAnswer.length > 0;

  if (!hasHints && !hasSteps && !hasAnswer) {
    return (
      <div className="gac-hint-box">
        <div className="gac-hint-row">
          <Icon
            name="hintsDoubtIcon"
            size={24}
            color={theme.colors.neutral[10]}
          />
          <p className="gac-hint-label">{t("lets_understand")}</p>
        </div>
        <div className="gac-hint-body">
          <ReactMarkdown {...mdPlugins}>
            {normalizeMarkdownMath(finalMarkdown)}
          </ReactMarkdown>
        </div>
      </div>
    );
  }

  const totalSteps = steps.length;
  const revealFullView = forceFullSolution;
  const solutionVisible = revealFullView || showSolution;
  const allStepsVisible = revealFullView || showAllSteps;
  const MAX_STEP_REVEALS = 2;
  const stepsRevealedCount = currentStepShowing + 1;
  const stepCap = Math.min(totalSteps, MAX_STEP_REVEALS);

  const canShowGiveAStep =
    !revealFullView &&
    !solutionVisible &&
    totalSteps > 0 &&
    currentStepShowing === -1;
  const canShowGiveAnotherStep =
    !revealFullView &&
    !solutionVisible &&
    !allStepsVisible &&
    currentStepShowing === 0 &&
    stepsRevealedCount < stepCap;
  const canShowGiveAllSteps =
    !revealFullView &&
    !solutionVisible &&
    !allStepsVisible &&
    currentStepShowing < totalSteps - 1;
  /** Offer final solution whenever we can show it or there are steps (parity with Angular). */
  const canShowGiveTheSolution =
    !revealFullView &&
    !solutionVisible &&
    (hasAnswer || totalSteps > 0 || hasHints);
  const canShowNoImDone = !revealFullView && !solutionVisible;

  const anyButtonVisible =
    canShowGiveAStep ||
    canShowGiveAnotherStep ||
    canShowGiveAllSteps ||
    canShowGiveTheSolution ||
    canShowNoImDone;

  const handleGiveAStep = () => {
    setCurrentStepShowing((prev) => {
      if (prev < 0) return 0;
      if (prev + 1 >= stepCap) return prev;
      return prev + 1;
    });
  };

  const handleGiveAllSteps = () => {
    setShowAllSteps(true);
    setCurrentStepShowing(totalSteps - 1);
    if (!hasAnswer && !solutionRevealedFiredRef.current) {
      solutionRevealedFiredRef.current = true;
      onSolutionRevealed?.();
    }
  };

  const handleGiveTheSolution = () => {
    setShowSolution(true);
    setShowAllSteps(true);
    setCurrentStepShowing(totalSteps - 1);
    if (!solutionRevealedFiredRef.current) {
      solutionRevealedFiredRef.current = true;
      onSolutionRevealed?.();
    }
  };

  const handleNoImDone = () => {
    onDoneResolved?.();
  };

  return (
    <>
      {hasHints
        ? hints.map((hint, index) => (
            <div className="gac-hint-box" key={`db-hint-${index}`}>
              <div className="gac-hint-row">
                <Icon
                  name="hintsDoubtIcon"
                  size={24}
                  color={theme.colors.neutral[10]}
                />
                <p className="gac-hint-label">{t("lets_understand")}</p>
              </div>
              <div className="gac-hint-body">
                <ReactMarkdown {...mdPlugins}>
                  {normalizeMarkdownMath(hint)}
                </ReactMarkdown>
              </div>
            </div>
          ))
        : null}

      {hasSteps && !allStepsVisible
        ? steps.map((step, index) => {
            if (index > currentStepShowing) return null;
            return (
              <div className="gac-steps-box" key={`db-step-card-${index}`}>
                <div className="gac-hint-row">
                  <Icon
                    name="stepsDoubtIcon"
                    size={24}
                    color={theme.colors.neutral[10]}
                  />
                  <p className="gac-steps-label">
                    {t("step")} {index + 1}
                  </p>
                </div>
                <div className="gac-step-desc">
                  <ReactMarkdown {...mdPlugins}>
                    {normalizeMarkdownMath(step)}
                  </ReactMarkdown>
                </div>
              </div>
            );
          })
        : null}

      {hasSteps && allStepsVisible ? (
        <div className="gac-steps-box">
          <div className="gac-hint-row">
            <Icon
              name="stepsDoubtIcon"
              size={24}
              color={theme.colors.neutral[10]}
            />
            <p className="gac-steps-label">{t("solution_steps")}</p>
          </div>
          {steps.map((step, index) => (
            <div key={`db-step-${index}`}>
              <p className="gac-step-title">
                {t("step")} {index + 1}:
              </p>
              <div className="gac-step-desc">
                <ReactMarkdown {...mdPlugins}>
                  {normalizeMarkdownMath(step)}
                </ReactMarkdown>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {hasAnswer && solutionVisible ? (
        <div className="gac-solution-box">
          <div className="gac-hint-row">
            <Icon
              name="solutionDoubtIcon"
              size={24}
              color={theme.colors.neutral[10]}
            />
            <p className="gac-solution-label">{t("solution")}</p>
          </div>
          <div className="gac-solution-body">
            <ReactMarkdown {...mdPlugins}>
              {normalizeMarkdownMath(displayAnswer)}
            </ReactMarkdown>
          </div>
        </div>
      ) : null}

      {anyButtonVisible ? (
        <div className="gac-tutor-btn-row">
          {canShowGiveAStep ? (
            <button
              type="button"
              className="gac-tutor-btn"
              onClick={handleGiveAStep}
            >
              {t("give_me_a_step")}
            </button>
          ) : null}
          {canShowGiveAnotherStep ? (
            <button
              type="button"
              className="gac-tutor-btn"
              onClick={handleGiveAStep}
            >
              {t("give_me_another_step")}
            </button>
          ) : null}
          {canShowGiveAllSteps ? (
            <button
              type="button"
              className="gac-tutor-btn"
              onClick={handleGiveAllSteps}
            >
              {t("give_me_all_steps")}
            </button>
          ) : null}
          {canShowGiveTheSolution ? (
            <button
              type="button"
              className="gac-tutor-btn"
              onClick={handleGiveTheSolution}
            >
              {t("give_me_the_solution")}
            </button>
          ) : null}
          {canShowNoImDone ? (
            <button
              type="button"
              className="gac-tutor-btn"
              onClick={handleNoImDone}
            >
              {t("no_im_done")}
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}
